import type { IIntegration } from '@repo/common/models/integration';
import { useCallback, useMemo } from 'react';
import {
	integrationsQueryKeyFactory,
	useUpdateIntegration,
} from '../../../api';
import {
	useActions as useIntegrationsActions,
	useColumns as useIntegrationsColumns,
} from '../../../pages/IntegrationsPage/IntegrationsPage.hooks';
import { IntegrationToggle } from '../../Integration/IntegrationToggle';
import type { ICommandListItem } from '../../Spotlight/components/CommandPalette/constants';
import type { ExtendedDataTableColumn } from '../../TableV2/types';

export function useColumns<T extends IIntegration>({
	readOnly = false,
}: {
	readOnly?: boolean;
}): ExtendedDataTableColumn<T>[] {
	const integrationColumns = useIntegrationsColumns();
	const { mutateAsync: updateIntegration } = useUpdateIntegration({
		invalidationKeys: [integrationsQueryKeyFactory.allLists()],
	});

	const handleChangeIntegration = useCallback(
		(field: string) => (id: string) => (value: unknown) => {
			updateIntegration({
				data: {
					id,
					[field]: value,
				},
			});
		},
		[updateIntegration]
	);

	const columns: ExtendedDataTableColumn<T>[] = useMemo(
		() => [
			...integrationColumns.filter(
				(column) =>
					column.accessor?.toLowerCase() !== 'status' &&
					column.accessor?.toLowerCase() !== 'teams' &&
					column.accessor?.toLowerCase() !== 'enabled' &&
					!column.accessor?.toLowerCase().startsWith('scheduled_extractions')
			),
			{
				accessor: 'enabled',
				titleLabel: 'State',
				navigate: false,
				render: (record: IIntegration) => (
					<IntegrationToggle
						enabled={record.enabled}
						onChange={handleChangeIntegration('enabled')(record.id)}
						disabled={readOnly}
					/>
				),
				width: 70,
			},
		],
		[integrationColumns, handleChangeIntegration, readOnly]
	);

	return columns;
}

export const useActions = (): ICommandListItem<IIntegration>[] => {
	const integrationActions = useIntegrationsActions();

	const actions: ICommandListItem<IIntegration>[] = useMemo(
		() => [
			...integrationActions.filter((action) => action.id !== 'actions::run'),
		],
		[integrationActions]
	);

	return actions;
};
